import { tv } from "@/utils/tv.config";
import { VariantProps } from "tailwind-variants";

export const chipClasses = tv({
  base: [
    "rounded-full",
    "border",
    "inline-flex",
    "items-center",
    "transition-all",
    "duration-150",
    "px-3",
    "py-1",
  ],
  variants: {
    variant: {
      filled: [
        "bg-primary-main",
        "text-primary-contrastText",
        "border-primary-main",
      ],
      outlined: [
        "bg-white",
        "border-primary-main",
        "text-primary-contrastText",
      ],
    },
    color: {
      default: [
        "bg-gray-50",
        "text-white",
        "border-gray-500",
        "hover:bg-gray-600",
        "hover:border-gray-600",
        "hover:text-gray-50",
      ],
      primary: [
        "bg-primary-50",
        "text-primary-main",
        "border-primary-main",
        "focus:ring focus:ring-primary-300",
        "disabled:cursor-not-allowed disabled:bg-primary-200 disabled:hover:bg-primary-200 disabled:active:bg-primary-200",
      ],
      secondary: [
        "text-secondary-600",
        "bg-secondary-50",
        "border-secondary-600",
        "focus:ring focus:ring-secondary-300",
        "disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:hover:bg-secondary-200 disabled:active:bg-secondary-200",
      ],
      error: [
        "text-red-500",
        "bg-red-50",
        "border-red-500",
        "focus:ring focus:ring-red-300",
        "disabled:cursor-not-allowed disabled:bg-red-200 disabled:hover:bg-red-200 disabled:active:bg-red-200",
      ],
      info: [
        "text-sky-500",
        "bg-sky-50",
        "border-sky-500",
        "focus:ring focus:ring-sky-300",
        "disabled:cursor-not-allowed disabled:bg-sky-200 disabled:hover:bg-sky-200 disabled:active:bg-sky-200",
      ],
      success: [
        "text-green-500",
        "bg-green-50",
        "border-green-500",
        "focus:ring focus:ring-green-300",
        "disabled:cursor-not-allowed disabled:bg-green-200 disabled:hover:bg-green-200 disabled:active:bg-green-200",
      ],
      warning: [
        "text-orange-500",
        "bg-orange-50",
        "border-orange-500",
        "focus:ring focus:ring-orange-300",
        "disabled:cursor-not-allowed disabled:bg-orange-200 disabled:hover:bg-orange-200 disabled:active:bg-orange-200",
      ],
    },
  },
  compoundVariants: [
    {
      variant: "filled",
      color: "default",
      className: [
        "text-gray-50",
        "bg-gray-500",
        "border-gray-500",
        "active:bg-gray-700",
        "focus:ring focus:ring-gray-300",
      ],
    },
    {
      variant: "filled",
      color: "primary",
      className: [
        "text-primary-50",
        "bg-primary-main",
        "border-primary-main",
        "active:bg-primary-700",
        "focus:ring focus:ring-primary-300",
      ],
    },
    {
      variant: "filled",
      color: "secondary",
      className: [
        "text-secondary-50",
        "bg-secondary-600",
        "border-secondary-600",
        "focus:ring focus:ring-secondary-300",
        "disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:hover:bg-secondary-200 disabled:active:bg-secondary-200",
      ],
    },
    {
      variant: "filled",
      color: "success",
      className: [
        "text-green-50",
        "bg-green-500",
        "border-green-500",
        "focus:ring focus:ring-green-300",
        "disabled:cursor-not-allowed disabled:bg-green-200 disabled:hover:bg-green-200 disabled:active:bg-green-200",
      ],
    },
    {
      variant: "filled",
      color: "info",
      className: [
        "text-sky-50",
        "bg-sky-500",
        "border-sky-500",
        "focus:ring focus:ring-sky-300",
        "disabled:cursor-not-allowed disabled:bg-sky-200 disabled:hover:bg-sky-200 disabled:active:bg-sky-200",
      ],
    },
    {
      variant: "filled",
      color: "warning",
      className: [
        "text-orange-50",
        "bg-orange-500",
        "border-orange-500",
        "focus:ring focus:ring-orange-300",
        "disabled:cursor-not-allowed disabled:bg-orange-200 disabled:hover:bg-orange-200 disabled:active:bg-orange-200",
      ],
    },
    {
      variant: "filled",
      color: "error",
      className: [
        "text-red-50",
        "bg-red-500",
        "border-red-500",
        "focus:ring focus:ring-red-300",
        "disabled:cursor-not-allowed disabled:bg-red-200 disabled:hover:bg-red-200 disabled:active:bg-red-200",
      ],
    },
  ],
  defaultVariants: {
    variant: "filled",
    color: "default",
  },
});

export const iconClasses = tv({
  base: ["h-4", "w-4", "rounded", "rounded-xl", "p-0.5"],
  variants: {
    size: {
      small: ["w-4", "h-4"],
      medium: ["w-5", "h-5"],
    },
    color: {
      default: ["bg-gray-500", "text-gray-50"],
      primary: ["bg-primary-main", "text-primary-50"],
      secondary: ["bg-secondary-500", "text-secondary-50"],
      info: ["bg-sky-500", "text-sky-50"],
      success: ["bg-green-500", "text-green-50"],
      warning: ["bg-orange-500", "text-orange-50"],
      error: ["bg-red-500", "text-red-50"],
    },
    variant: {
      filled: [],
      outlined: [],
    },
  },
  compoundVariants: [
    {
      variant: "filled",
      color: "default",
      class: ["bg-gray-400", "text-gray-50"],
    },
    {
      variant: "filled",
      color: "primary",
      class: ["bg-primary-50", "text-primary-main"],
    },
    {
      variant: "filled",
      color: "secondary",
      class: ["bg-secondary-50", "text-secondary-500"],
    },
    {
      variant: "filled",
      color: "info",
      class: ["bg-sky-50", "text-sky-500"],
    },
    {
      variant: "filled",
      color: "success",
      class: ["bg-green-50", "text-green-500"],
    },
    {
      variant: "filled",
      color: "warning",
      class: ["bg-orange-50", "text-orange-500"],
    },
    {
      variant: "filled",
      color: "error",
      class: ["bg-red-50", "text-red-500"],
    },
  ],
  defaultVariants: {
    size: "medium",
  },
});

export type ChipVariants = VariantProps<typeof chipClasses>;

export type ChipIconVariants = VariantProps<typeof iconClasses>;
